import { forwardRef, useEffect, useMemo, useRef, useState } from "react";
import { Input } from "@/components/ui/input";
import { Calendar as CalendarIcon } from "@phosphor-icons/react";

const MIN_DATE = "2000-01-01";
const MAX_DATE = "2099-12-31";

/**
 * DateInput
 * -----------------------------------------------------------------------------
 * Reemplazo drop-in de `<Input type="date">`. Antes usábamos el input nativo
 * pero Chromium borra el día/mes al completar el año (UX rota).
 *
 * Ahora:
 *  - Input de texto con máscara "DD/MM/AAAA" — el usuario tipea fluido, se
 *    auto-insertan las "/" y NUNCA se borra la parte ya escrita.
 *  - Un botón calendario abre el picker nativo (para elegir con click).
 *  - Al perder foco parsea a ISO (`YYYY-MM-DD`) y llama `onChange(e)` con el
 *    formato que espera el resto del sistema (compatible con `type="date"`).
 *  - Valor externo (prop `value`, ISO) se muestra formateado como DD/MM/AAAA.
 */
const isValidIso = (s) => /^\d{4}-\d{2}-\d{2}$/.test(String(s || ""));

const isoToDmy = (iso) => {
  if (!iso || !isValidIso(iso)) return "";
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y}`;
};

const dmyToIso = (dmy) => {
  if (!dmy) return "";
  // Extrae dígitos y arma ISO. Tolerante a "1/1/26" → "2026-01-01".
  const nums = String(dmy).match(/\d+/g) || [];
  if (nums.length < 3) return "";
  let [d, m, y] = nums;
  if (!d || !m || !y) return "";
  if (y.length === 2) y = (parseInt(y, 10) < 70 ? "20" : "19") + y;
  if (y.length !== 4) return "";
  d = d.padStart(2, "0"); m = m.padStart(2, "0");
  const iso = `${y}-${m}-${d}`;
  return isValidIso(iso) ? iso : "";
};

const maskDmy = (raw) => {
  // Deja pasar sólo dígitos y auto-inserta "/" tras el día y el mes.
  const digits = String(raw || "").replace(/\D/g, "").slice(0, 8);
  const p1 = digits.slice(0, 2);
  const p2 = digits.slice(2, 4);
  const p3 = digits.slice(4, 8);
  if (digits.length <= 2) return p1;
  if (digits.length <= 4) return `${p1}/${p2}`;
  return `${p1}/${p2}/${p3}`;
};

const DateInput = forwardRef(function DateInput(
  { value, onChange, onBlur, min = MIN_DATE, max = MAX_DATE, className = "", ...rest },
  ref,
) {
  const [text, setText] = useState(() => isoToDmy(value));
  const nativeRef = useRef(null);
  const externalValue = value || "";

  // Sincroniza el texto cuando cambia el value externo desde afuera (ej. reset,
  // set desde otro control). Sólo si el usuario no está tipeando algo distinto.
  useEffect(() => {
    const fromProps = isoToDmy(externalValue);
    setText((cur) => {
      if (cur === "") return fromProps;
      const curIso = dmyToIso(cur);
      if (curIso === externalValue) return cur;
      return fromProps;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [externalValue]);

  const handleTextChange = (e) => {
    const masked = maskDmy(e.target.value);
    setText(masked);
    // Si el usuario terminó de tipear los 8 dígitos y es válido, propagamos
    // el ISO en vivo — así el usuario no depende del blur.
    if (masked.length === 10) {
      const iso = dmyToIso(masked);
      if (iso && (!min || iso >= min) && (!max || iso <= max)) {
        onChange && onChange({ ...e, target: { ...e.target, value: iso } });
      }
    } else if (masked === "" && externalValue) {
      // Borró todo: propagamos vacío.
      onChange && onChange({ ...e, target: { ...e.target, value: "" } });
    }
  };

  const handleBlur = (e) => {
    const iso = dmyToIso(text);
    if (iso) {
      let final = iso;
      if (min && final < min) final = min;
      if (max && final > max) final = max;
      setText(isoToDmy(final));
      if (final !== externalValue) {
        onChange && onChange({ ...e, target: { ...e.target, value: final } });
      }
    } else if (text === "" && externalValue) {
      onChange && onChange({ ...e, target: { ...e.target, value: "" } });
    } else if (text) {
      // Texto inválido: revertimos al value externo.
      setText(isoToDmy(externalValue));
    }
    onBlur && onBlur(e);
  };

  const openPicker = () => {
    const el = nativeRef.current;
    if (!el) return;
    if (typeof el.showPicker === "function") {
      try { el.showPicker(); return; } catch (_) {}
    }
    el.focus();
    el.click();
  };

  const handleNativeChange = (e) => {
    const iso = e.target.value;
    if (!iso) return;
    setText(isoToDmy(iso));
    onChange && onChange({ ...e, target: { ...e.target, value: iso } });
  };

  // Ancho útil por si el consumidor no pasa className con w-*.
  return (
    <div className={`relative inline-flex items-stretch ${className.includes("w-") ? "" : "w-full"}`}>
      <Input
        ref={ref}
        type="text"
        inputMode="numeric"
        placeholder="DD/MM/AAAA"
        value={text}
        onChange={handleTextChange}
        onBlur={handleBlur}
        maxLength={10}
        className={`pr-9 ${className}`}
        {...rest}
      />
      <button
        type="button"
        onClick={openPicker}
        tabIndex={-1}
        aria-label="Abrir calendario"
        className="absolute right-1 top-1/2 -translate-y-1/2 p-1 rounded-sm text-neutral-500 hover:text-neutral-800 hover:bg-neutral-100"
      >
        <CalendarIcon size={14} weight="bold" />
      </button>
      <input
        ref={nativeRef}
        type="date"
        min={min}
        max={max}
        value={externalValue || ""}
        onChange={handleNativeChange}
        className="sr-only"
        tabIndex={-1}
        aria-hidden="true"
      />
    </div>
  );
});

export default DateInput;
export { DateInput };
